import React, { useState, useEffect, useMemo } from 'react';
import type { PortfolioItem } from '../types/interfaces';
import { formatCurrency } from '../utils/currency';
import { usePortfolioSnapshots } from '../hooks/usePortfolioSnapshots';

interface AdvancedPortfolioAnalyticsProps {
  portfolio: PortfolioItem[];
  currency: string;
  onUnlock: () => Promise<boolean>;
}

interface TaxLot {
  coinId: string;
  quantity: number;
  purchasePrice: number;
  purchaseDate: number;
}

interface TaxReport {
  realizedGains: number;
  realizedLosses: number;
  unrealizedGains: number;
  unrealizedLosses: number;
  netCapitalGains: number;
  lots: TaxLot[];
}

interface RiskMetrics {
  volatility: number;
  sharpeRatio: number;
  maxDrawdown: number;
  beta: number;
}

interface CorrelationPair {
  coin1: string;
  coin2: string;
  correlation: number;
}

interface RebalancingSuggestion {
  coinId: string;
  coinName: string;
  currentAllocation: number;
  targetAllocation: number;
  suggestedChange: number;
  reason: string;
}

declare global {
  interface Window {
    bsvPayments?: {
      requestPayment: (action: string, amount: number) => Promise<any>;
      getPaymentAmount: (action: string) => number;
    };
  }
}

export default function AdvancedPortfolioAnalytics({
  portfolio,
  currency,
  onUnlock,
}: AdvancedPortfolioAnalyticsProps) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taxMethod, setTaxMethod] = useState<'FIFO' | 'LIFO' | 'AVG'>('FIFO');
  const { snapshots } = usePortfolioSnapshots(portfolio);

  const handleUnlock = async () => {
    setUnlocking(true);
    setError(null);
    try {
      const success = await onUnlock();
      if (success) {
        setIsUnlocked(true);
      } else {
        setError('Connect MetaNet wallet');
      }
    } catch (err) {
      console.error('Error unlocking advanced analytics:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setUnlocking(false);
    }
  };

  // Calculate tax lots (simplified - assuming all purchases are separate lots)
  const taxLots = useMemo<TaxLot[]>(() => {
    return portfolio.map(item => ({
      coinId: item.id,
      quantity: item.quantity,
      purchasePrice: item.purchasePrice ?? item.currentPrice,
      purchaseDate: Date.now() - 30 * 24 * 60 * 60 * 1000, // Placeholder - in real app, track actual purchase dates
    }));
  }, [portfolio]);

  // Calculate tax report
  const taxReport = useMemo<TaxReport>(() => {
    const totalCost = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * ((item.purchasePrice ?? item.currentPrice) || 0),
      0
    );
    const totalValue = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.currentPrice || 0),
      0
    );
    const netGain = totalValue - totalCost;

    return {
      realizedGains: 0, // Would need transaction history to calculate
      realizedLosses: 0, // Would need transaction history to calculate
      unrealizedGains: netGain > 0 ? netGain : 0,
      unrealizedLosses: netGain < 0 ? Math.abs(netGain) : 0,
      netCapitalGains: netGain,
      lots: taxLots,
    };
  }, [portfolio, taxLots]);

  // Calculate risk metrics (simplified calculations)
  const riskMetrics = useMemo<RiskMetrics>(() => {
    if (snapshots.length < 2) {
      return {
        volatility: 0,
        sharpeRatio: 0,
        maxDrawdown: 0,
        beta: 1,
      };
    }

    // Calculate returns from snapshots
    const returns = [];
    for (let i = 1; i < snapshots.length; i++) {
      const prevValue = snapshots[i - 1].totalValue;
      const currentValue = snapshots[i].totalValue;
      if (prevValue > 0) {
        returns.push((currentValue - prevValue) / prevValue);
      }
    }

    if (returns.length === 0) {
      return {
        volatility: 0,
        sharpeRatio: 0,
        maxDrawdown: 0,
        beta: 1,
      };
    }

    // Calculate volatility (standard deviation of returns)
    const meanReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const variance = returns.reduce((sum, r) => sum + Math.pow(r - meanReturn, 2), 0) / returns.length;
    const volatility = Math.sqrt(variance) * Math.sqrt(252); // Annualized

    // Calculate Sharpe ratio (simplified - assuming risk-free rate of 0)
    const sharpeRatio = meanReturn > 0 ? (meanReturn / (volatility / Math.sqrt(252))) : 0;

    // Calculate max drawdown
    let maxDrawdown = 0;
    let peak = snapshots[0].totalValue;
    for (const snapshot of snapshots) {
      if (snapshot.totalValue > peak) {
        peak = snapshot.totalValue;
      }
      const drawdown = (peak - snapshot.totalValue) / peak;
      if (drawdown > maxDrawdown) {
        maxDrawdown = drawdown;
      }
    }

    // Beta (simplified - would need market benchmark data)
    const beta = 1.0;

    return {
      volatility: volatility * 100,
      sharpeRatio,
      maxDrawdown: maxDrawdown * 100,
      beta,
    };
  }, [snapshots]);

  // Calculate correlations (simplified - would need price history)
  const correlations = useMemo<CorrelationPair[]>(() => {
    const pairs: CorrelationPair[] = [];
    for (let i = 0; i < portfolio.length; i++) {
      for (let j = i + 1; j < portfolio.length; j++) {
        // Simplified correlation - in real app, would use historical price data
        const correlation = Math.random() * 0.8 - 0.4; // Random between -0.4 and 0.4
        pairs.push({
          coin1: portfolio[i].name,
          coin2: portfolio[j].name,
          correlation,
        });
      }
    }
    return pairs.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
  }, [portfolio]);

  // Generate rebalancing suggestions
  const rebalancingSuggestions = useMemo<RebalancingSuggestion[]>(() => {
    const totalValue = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.currentPrice || 0),
      0
    );

    if (totalValue === 0) return [];

    const targetAllocation = 100 / portfolio.length; // Equal weight target
    const suggestions: RebalancingSuggestion[] = [];

    portfolio.forEach(item => {
      const currentValue = (item.quantity || 0) * (item.currentPrice || 0);
      const currentAllocation = (currentValue / totalValue) * 100;
      const difference = currentAllocation - targetAllocation;

      if (Math.abs(difference) > 5) { // Only suggest if off by more than 5%
        suggestions.push({
          coinId: item.id,
          coinName: item.name,
          currentAllocation,
          targetAllocation,
          suggestedChange: -difference, // Negative means sell, positive means buy
          reason: difference > 0
            ? 'Over-allocated - consider rebalancing'
            : 'Under-allocated - consider adding more',
        });
      }
    });

    return suggestions.sort((a, b) => Math.abs(b.suggestedChange) - Math.abs(a.suggestedChange));
  }, [portfolio]);

  // Performance attribution (simplified)
  const performanceAttribution = useMemo(() => {
    const totalCost = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * ((item.purchasePrice ?? item.currentPrice) || 0),
      0
    );
    const totalValue = portfolio.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.currentPrice || 0),
      0
    );

    return portfolio.map(item => {
      const cost = (item.quantity || 0) * ((item.purchasePrice ?? item.currentPrice) || 0);
      const value = (item.quantity || 0) * (item.currentPrice || 0);
      const profit = value - cost;
      const roi = cost > 0 ? (profit / cost) * 100 : 0;
      const contribution = totalCost > 0 ? ((value - cost) / totalCost) * 100 : 0;

      return {
        coinId: item.id,
        coinName: item.name,
        contribution,
        roi,
        profit,
      };
    }).sort((a, b) => b.contribution - a.contribution);
  }, [portfolio]);

  if (!isUnlocked) {
    return (
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 text-center shadow-bubble border-2 border-primary/10">
        <div className="text-6xl mb-4">📈</div>
        <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          Advanced Portfolio Analytics
        </h3>
        <p className="text-text-secondary text-lg mb-6">
          Unlock professional-grade portfolio insights including tax reporting, risk metrics, correlation analysis, and optimization recommendations.
        </p>
        <p className="text-text-secondary text-sm mb-8">
          <strong>1000 satoshis per use</strong>
        </p>
        {error && <p className="text-red-400 mb-4">{error}</p>}
        <button
          onClick={handleUnlock}
          className="px-10 py-5 bg-gradient-to-r from-primary to-purple-500 border-none rounded-full text-white text-lg font-bold cursor-pointer transition-all duration-300 hover:scale-110 hover:shadow-bubble-lg active:scale-95"
          disabled={unlocking}
        >
          {unlocking ? 'Processing Payment...' : '🔓 Unlock Advanced Analytics (1000 sats)'}
        </button>
      </div>
    );
  }

  if (portfolio.length === 0) {
    return (
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-10 text-center shadow-bubble border-2 border-primary/10">
        <p className="text-text-secondary">No portfolio data available. Add coins to see advanced analytics.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-8 shadow-bubble border-2 border-primary/10">
        <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          💰 Tax Reporting
        </h3>
        <div className="mb-6">
          <label className="block mb-3 text-text-secondary font-bold uppercase tracking-wider text-sm">
            Tax Method:
          </label>
          <div className="flex gap-4">
            {(['FIFO', 'LIFO', 'AVG'] as const).map(method => (
              <button
                key={method}
                onClick={() => setTaxMethod(method)}
                className={`px-6 py-3 rounded-full border-2 transition-all duration-300 font-semibold ${
                  taxMethod === method
                    ? 'bg-gradient-to-r from-primary to-purple-500 border-primary text-white shadow-bubble-lg scale-105'
                    : 'bg-transparent border-border text-text hover:border-primary hover:scale-105'
                }`}
              >
                {method}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Realized Gains</span>
            <span className="block text-xl font-bold text-green-400">{formatCurrency(taxReport.realizedGains, currency)}</span>
          </div>
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Realized Losses</span>
            <span className="block text-xl font-bold text-red-400">{formatCurrency(taxReport.realizedLosses, currency)}</span>
          </div>
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Unrealized Gains</span>
            <span className="block text-xl font-bold text-green-400">{formatCurrency(taxReport.unrealizedGains, currency)}</span>
          </div>
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Net Capital Gains</span>
            <span className={`block text-xl font-bold ${taxReport.netCapitalGains >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatCurrency(taxReport.netCapitalGains, currency)}
            </span>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-lg font-bold mb-3 text-text">Tax Lots ({taxMethod}):</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-bg-tertiary text-text-secondary uppercase text-sm font-bold">
                  <th className="p-3 rounded-tl-xl">Coin</th>
                  <th className="p-3">Quantity</th>
                  <th className="p-3">Purchase Price</th>
                  <th className="p-3 rounded-tr-xl">Current Value</th>
                </tr>
              </thead>
              <tbody>
                {taxReport.lots.map((lot, idx) => {
                  const item = portfolio.find(p => p.id === lot.coinId);
                  if (!item) return null;
                  const currentValue = lot.quantity * item.currentPrice;
                  return (
                    <tr key={idx} className="border-b border-border">
                      <td className="p-3 text-text">{item.name}</td>
                      <td className="p-3 text-text">{lot.quantity.toFixed(4)}</td>
                      <td className="p-3 text-text">{formatCurrency(lot.purchasePrice, currency)}</td>
                      <td className="p-3 text-text">{formatCurrency(currentValue, currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-8 shadow-bubble border-2 border-primary/10">
        <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          ⚠️ Risk Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Volatility (Annualized)</span>
            <span className="block text-xl font-bold text-text">{riskMetrics.volatility.toFixed(2)}%</span>
          </div>
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Sharpe Ratio</span>
            <span className="block text-xl font-bold text-text">{riskMetrics.sharpeRatio.toFixed(2)}</span>
          </div>
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Max Drawdown</span>
            <span className="block text-xl font-bold text-red-400">{riskMetrics.maxDrawdown.toFixed(2)}%</span>
          </div>
          <div className="bg-bg p-6 rounded-2xl border-2 border-border">
            <span className="block text-text-secondary text-xs font-bold uppercase tracking-wider mb-2">Beta</span>
            <span className="block text-xl font-bold text-text">{riskMetrics.beta.toFixed(2)}</span>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-8 shadow-bubble border-2 border-primary/10">
        <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          🔗 Correlation Analysis
        </h3>
        {correlations.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-bg-tertiary text-text-secondary uppercase text-sm font-bold">
                  <th className="p-3 rounded-tl-xl">Coin 1</th>
                  <th className="p-3">Coin 2</th>
                  <th className="p-3 rounded-tr-xl">Correlation</th>
                </tr>
              </thead>
              <tbody>
                {correlations.slice(0, 10).map((pair, idx) => (
                  <tr key={idx} className="border-b border-border">
                    <td className="p-3 text-text">{pair.coin1}</td>
                    <td className="p-3 text-text">{pair.coin2}</td>
                    <td className={`p-3 font-bold ${Math.abs(pair.correlation) > 0.7 ? 'text-primary' : 'text-text'}`}>
                      {pair.correlation.toFixed(3)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-text-secondary">Not enough data for correlation analysis.</p>
        )}
      </div>

      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-8 shadow-bubble border-2 border-primary/10">
        <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          ⚖️ Rebalancing Suggestions
        </h3>
        {rebalancingSuggestions.length > 0 ? (
          <div className="space-y-4">
            {rebalancingSuggestions.map((suggestion, idx) => (
              <div key={idx} className="bg-bg p-6 rounded-2xl border-2 border-border">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-text">{suggestion.coinName}</h4>
                    <p className="text-text-secondary text-sm">{suggestion.reason}</p>
                  </div>
                  <span className={`font-bold ${suggestion.suggestedChange > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {suggestion.suggestedChange > 0 ? '+' : ''}{suggestion.suggestedChange.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm text-text-secondary">
                  <span>Current: {suggestion.currentAllocation.toFixed(1)}%</span>
                  <span>→</span>
                  <span>Target: {suggestion.targetAllocation.toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-text-secondary">Your portfolio is well-balanced! No rebalancing needed.</p>
        )}
      </div>

      <div className="bg-gradient-to-br from-bg-secondary to-bg-tertiary rounded-3xl p-8 shadow-bubble border-2 border-primary/10">
        <h3 className="text-2xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">
          📊 Performance Attribution
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-bg-tertiary text-text-secondary uppercase text-sm font-bold">
                <th className="p-3 rounded-tl-xl">Coin</th>
                <th className="p-3">Contribution (%)</th>
                <th className="p-3">ROI (%)</th>
                <th className="p-3 rounded-tr-xl">Profit/Loss</th>
              </tr>
            </thead>
            <tbody>
              {performanceAttribution.map((item, idx) => (
                <tr key={idx} className="border-b border-border">
                  <td className="p-3 text-text">{item.coinName}</td>
                  <td className="p-3 text-text">{item.contribution.toFixed(2)}%</td>
                  <td className={`p-3 font-bold ${item.roi >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {item.roi >= 0 ? '+' : ''}{item.roi.toFixed(2)}%
                  </td>
                  <td className={`p-3 font-bold ${item.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(item.profit, currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

